import { loadImageElement } from './patternTexture'

/**
 * The artwork's SILHOUETTE, traced from its alpha channel.
 *
 * A logo used to be a rectangle that showed its shape through the transparency of its
 * texture. Everything that reads glTF alpha drew it correctly, but everything that does
 * not — Blender's solid viewport, the old customizer's material swap, plenty of CAD
 * importers — drew the rectangle. Cutting the rectangle down to the traced outline moves
 * the shape into the geometry, where nothing can ignore it.
 *
 * Points are in the artwork's own UV space: u = 0..1 across, v = 0..1 DOWN (matching the
 * flipY:false textures used here), so the same values index both the mesh and its texture.
 */
export interface LogoOutlineShape {
  /** Outer boundary, counter-clockwise in a y-up reading of the UVs. */
  outer: number[][]
  /** Enclosed openings — the counter of an "A", the ring of an ®. Clockwise. */
  holes: number[][][]
}

export interface LogoOutline {
  shapes: LogoOutlineShape[]
  /** Points in the whole outline, for the vertex budget and for reporting. */
  pointCount: number
}

/**
 * Longest edge of the copy the outline is traced from.
 *
 * Artwork is cropped to the mark before it gets here, and a logo file is often small — the
 * one on this tumbler is 121px wide. Tracing at whatever size it happens to be gives a
 * handful of pixels around a letter's bowl, and the outline comes out a polygon: an "o" as
 * an octagon. So the mark is resampled to a fixed working size, up as well as down, and the
 * smooth edges that the browser's bilinear filtering produces are what the tracer follows.
 * The simplifier below, not this, is what keeps the point count sane.
 */
const TRACE_DIM = 1024

/** Bounds on the resample, so a huge file is not traced pixel-by-pixel nor a tiny one blown up absurdly. */
const TRACE_SCALE_RANGE: [number, number] = [0.25, 8]

/** Alpha (0..255) at which the artwork is considered solid — the middle of its edge ramp. */
const ALPHA_THRESHOLD = 128

/** A traced loop smaller than this (in pixels²) is a speck, not a mark. */
const MIN_LOOP_AREA_PX = 4

/** Upper bound on traced points; the simplifier is loosened until the outline fits. */
const MAX_POINTS = 4000

/** Simplification tolerance in pixels, and how far it is allowed to grow to hit the budget. */
const BASE_EPSILON_PX = 0.4
const MAX_EPSILON_PX = 6

/**
 * White-knockout grading, matching removeWhiteBackground in patternTexture.ts: artwork that
 * arrived as a mark on a white card has its backdrop read as transparent.
 */
const KNOCKOUT_CLEAR = 250
const KNOCKOUT_KEEP = 235

/** Traced outlines, keyed by artwork. Tracing is one pass over a downscaled copy; reuse it. */
const cache = new Map<string, LogoOutline | null>()

/** The outline for this artwork if it has already been traced. Null = traced, nothing usable. */
export function cachedLogoOutline(image: string | undefined): LogoOutline | null | undefined {
  return image ? cache.get(image) : undefined
}

/**
 * Trace this artwork's silhouette, caching the result. Returns null when the artwork cannot
 * be traced (unreadable, or nothing solid in it) — the caller then keeps the plain sheet.
 */
export async function ensureLogoOutline(image: string, knockoutWhite: boolean): Promise<LogoOutline | null> {
  const hit = cache.get(image)
  if (hit !== undefined) return hit
  let outline: LogoOutline | null = null
  try {
    outline = traceOutline(await loadImageElement(image), knockoutWhite)
  } catch {
    outline = null // unreadable or tainted artwork: fall back to the rectangular sheet
  }
  cache.set(image, outline)
  return outline
}

function traceOutline(image: HTMLImageElement, knockoutWhite: boolean): LogoOutline | null {
  const fit = TRACE_DIM / Math.max(image.width, image.height, 1)
  const scale = Math.min(TRACE_SCALE_RANGE[1], Math.max(TRACE_SCALE_RANGE[0], fit))
  const w = Math.max(1, Math.round(image.width * scale))
  const h = Math.max(1, Math.round(image.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(image, 0, 0, w, h)

  let px: Uint8ClampedArray
  try {
    px = ctx.getImageData(0, 0, w, h).data
  } catch {
    return null
  }

  // Solid/clear grid, padded by one cell all round so every loop closes inside the grid.
  const gw = w + 2
  const gh = h + 2
  const mask = new Uint8Array(gw * gh)
  let solid = 0
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4
      let alpha = px[i + 3]
      if (knockoutWhite) {
        const darkest = Math.min(px[i], px[i + 1], px[i + 2])
        if (darkest >= KNOCKOUT_CLEAR) alpha = 0
        else if (darkest > KNOCKOUT_KEEP)
          alpha = (alpha * (KNOCKOUT_CLEAR - darkest)) / (KNOCKOUT_CLEAR - KNOCKOUT_KEEP)
      }
      if (alpha >= ALPHA_THRESHOLD) {
        mask[(y + 1) * gw + (x + 1)] = 1
        solid++
      }
    }
  }
  if (!solid) return null

  const loops = marchingSquares(mask, gw, gh)
  if (!loops.length) return null

  // Budget: simplify harder until the outline fits, rather than shipping a logo whose
  // outline costs more triangles than the product it sits on.
  let epsilon = BASE_EPSILON_PX
  let simplified = loops.map((loop) => simplifyLoop(loop, epsilon))
  while (countPoints(simplified) > MAX_POINTS && epsilon < MAX_EPSILON_PX) {
    epsilon *= 1.6
    simplified = loops.map((loop) => simplifyLoop(loop, epsilon))
  }

  // Keep real marks only, then sort big-to-small so containment tests see parents first.
  const kept = simplified
    .map((points) => ({ points, area: signedArea(points) }))
    .filter((l) => l.points.length >= 3 && Math.abs(l.area) >= MIN_LOOP_AREA_PX)
    .sort((a, b) => Math.abs(b.area) - Math.abs(a.area))
  if (!kept.length) return null

  // Nesting decides what is a hole, not the tracing direction: a loop inside an odd number
  // of other loops is an opening (the counter of an "A"), inside an even number it is a
  // mark of its own (the dot of an "i", a second word).
  const shapes: LogoOutlineShape[] = []
  const shapeOf: number[] = []
  kept.forEach((loop, i) => {
    let depth = 0
    let parent = -1
    for (let j = 0; j < i; j++) {
      if (pointInLoop(loop.points[0], kept[j].points)) {
        depth++
        parent = j
      }
    }
    if (depth % 2 === 0) {
      shapeOf[i] = shapes.length
      shapes.push({ outer: orient(loop.points, true, w, h), holes: [] })
    } else {
      const owner = shapeOf[parent]
      if (owner !== undefined) shapes[owner].holes.push(orient(loop.points, false, w, h))
    }
  })

  const withOutline = shapes.filter((s) => s.outer.length >= 3)
  if (!withOutline.length) return null
  return { shapes: withOutline, pointCount: countPoints(withOutline.flatMap((s) => [s.outer, ...s.holes])) }
}

/** Pixel loop → UV points, wound for three's triangulator (outer CCW, holes CW, y up). */
function orient(points: number[][], outer: boolean, w: number, h: number): number[][] {
  // In a y-up reading of the UVs the sign of the area flips, hence the negation.
  const ccw = -signedArea(points) > 0
  const ordered = ccw === outer ? points : [...points].reverse()
  return ordered.map(([x, y]) => [x / w, y / h])
}

function countPoints(loops: number[][][]): number {
  return loops.reduce((n, l) => n + l.length, 0)
}

/**
 * Marching squares over the solid/clear grid, producing closed loops in pixel coordinates.
 *
 * Each cell contributes segments between the midpoints of its edges; the segments are then
 * chained end to end into loops. The two ambiguous cases (diagonally opposite corners) are
 * settled by the cell's own centre, which keeps every junction at degree two and so keeps
 * the chaining unambiguous.
 */
function marchingSquares(mask: Uint8Array, gw: number, gh: number): number[][][] {
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= gw || y >= gh ? 0 : mask[y * gw + x])
  const segments: number[][] = [] // [x0, y0, x1, y1] at half-pixel resolution (integers ×2)
  const push = (x0: number, y0: number, x1: number, y1: number) => segments.push([x0, y0, x1, y1])

  for (let y = 0; y < gh - 1; y++) {
    for (let x = 0; x < gw - 1; x++) {
      const tl = at(x, y)
      const tr = at(x + 1, y)
      const br = at(x + 1, y + 1)
      const bl = at(x, y + 1)
      const code = (tl << 3) | (tr << 2) | (br << 1) | bl
      if (code === 0 || code === 15) continue
      // Edge midpoints, in ×2 integer coordinates so endpoints match exactly between cells.
      const X = x * 2
      const Y = y * 2
      const top = [X + 1, Y]
      const right = [X + 2, Y + 1]
      const bottom = [X + 1, Y + 2]
      const left = [X, Y + 1]
      const seg = (a: number[], b: number[]) => push(a[0], a[1], b[0], b[1])
      switch (code) {
        case 1:
          seg(left, bottom)
          break
        case 2:
          seg(bottom, right)
          break
        case 3:
          seg(left, right)
          break
        case 4:
          seg(top, right)
          break
        case 5:
          // Saddle: the centre says whether the two corners join or stay apart.
          if (tl || br) {
            seg(left, top)
            seg(bottom, right)
          } else {
            seg(left, bottom)
            seg(top, right)
          }
          break
        case 6:
          seg(top, bottom)
          break
        case 7:
          seg(left, top)
          break
        case 8:
          seg(top, left)
          break
        case 9:
          seg(top, bottom)
          break
        case 10:
          if (tr || bl) {
            seg(top, right)
            seg(bottom, left)
          } else {
            seg(top, left)
            seg(bottom, right)
          }
          break
        case 11:
          seg(top, right)
          break
        case 12:
          seg(left, right)
          break
        case 13:
          seg(bottom, right)
          break
        case 14:
          seg(left, bottom)
          break
      }
    }
  }
  if (!segments.length) return []

  // Chain segments into loops through their shared endpoints.
  const key = (x: number, y: number) => x * 100000 + y
  const ends = new Map<number, number[]>()
  segments.forEach((s, i) => {
    for (const [x, y] of [
      [s[0], s[1]],
      [s[2], s[3]],
    ]) {
      const k = key(x, y)
      const list = ends.get(k)
      if (list) list.push(i)
      else ends.set(k, [i])
    }
  })

  const used = new Uint8Array(segments.length)
  const loops: number[][][] = []
  for (let start = 0; start < segments.length; start++) {
    if (used[start]) continue
    used[start] = 1
    const s = segments[start]
    const loop: number[][] = [
      [s[0] / 2, s[1] / 2],
      [s[2] / 2, s[3] / 2],
    ]
    let cx = s[2]
    let cy = s[3]
    for (;;) {
      const candidates = ends.get(key(cx, cy))
      const next = candidates?.find((i) => !used[i])
      if (next === undefined) break
      used[next] = 1
      const n = segments[next]
      const atStart = n[0] === cx && n[1] === cy
      cx = atStart ? n[2] : n[0]
      cy = atStart ? n[3] : n[1]
      loop.push([cx / 2, cy / 2])
    }
    // Closed loops only: an open chain means a malformed trace, and a mesh built from one
    // would have a slit in it.
    const first = loop[0]
    const last = loop[loop.length - 1]
    if (loop.length >= 4 && first[0] === last[0] && first[1] === last[1]) {
      loop.pop()
      loops.push(loop)
    }
  }
  return loops
}

/** Ramer–Douglas–Peucker on a closed loop. */
function simplifyLoop(loop: number[][], epsilon: number): number[][] {
  if (loop.length < 4) return loop
  // Cut the loop at its two most distant-in-sequence points so the simplifier sees open
  // chains; simplifying a closed ring directly collapses it onto its endpoints.
  const half = Math.floor(loop.length / 2)
  const a = simplifyChain(loop.slice(0, half + 1), epsilon)
  const b = simplifyChain([...loop.slice(half), loop[0]], epsilon)
  return [...a.slice(0, -1), ...b.slice(0, -1)]
}

function simplifyChain(points: number[][], epsilon: number): number[][] {
  if (points.length < 3) return points
  let maxDist = 0
  let index = 0
  const [x0, y0] = points[0]
  const [x1, y1] = points[points.length - 1]
  for (let i = 1; i < points.length - 1; i++) {
    const d = pointSegmentDistance(points[i][0], points[i][1], x0, y0, x1, y1)
    if (d > maxDist) {
      maxDist = d
      index = i
    }
  }
  if (maxDist <= epsilon) return [points[0], points[points.length - 1]]
  const left = simplifyChain(points.slice(0, index + 1), epsilon)
  const right = simplifyChain(points.slice(index), epsilon)
  return [...left.slice(0, -1), ...right]
}

function pointSegmentDistance(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0
  const dy = y1 - y0
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(px - x0, py - y0)
  const t = Math.max(0, Math.min(1, ((px - x0) * dx + (py - y0) * dy) / lenSq))
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy))
}

function signedArea(points: number[][]): number {
  let sum = 0
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    sum += (points[j][0] - points[i][0]) * (points[j][1] + points[i][1])
  }
  return sum / 2
}

function pointInLoop(point: number[], loop: number[][]): boolean {
  const [x, y] = point
  let inside = false
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const [xi, yi] = loop[i]
    const [xj, yj] = loop[j]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}
